import { FAKE, inject, REAL, registerDependency } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createSignal } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { createUrl } from '@cloudsuite/mosaic-shared/scripts/base-url.js'
import { validate } from 'typia'
import {
  CreateOrderTemplateRequest,
  GetOrderTemplatesRequest,
  ORDER_TEMPLATES_API,
  type OrderTemplatesAPI,
} from 'components/discovery/product/add-to-order-templates/api/order-templates-api.js'
import {
  ADD_TO_ORDER_TEMPLATES_API,
  AddProductToTemplateRequest,
  RemoveProductFromTemplateRequest,
  type AddProductToTemplateResponse,
  type AddToOrderTemplatesApi,
  type RemoveProductFromTemplateResponse,
} from 'components/discovery/product/add-to-order-templates/api/add-to-order-templates-api.js'
import type { OrderTemplates } from 'components/discovery/product/add-to-order-templates/typing/order-templates-types.js'
import { ecommerceApiTokenStore } from 'components/discovery/shared/ecommerce-api-token/stores/ecommerce-api-token-store.js'
import { validationError } from 'components/discovery/shared/validation.js'
import { orderTemplatesPath, tokenPath } from 'components/discovery/shared/endpoint-paths.js'

export const ORDER_TEMPLATES_STORE = Symbol('ORDER_TEMPLATES_STORE')

export class OrderTemplatesNotConfiguredError extends Error {
  constructor() {
    super('Order templates store has no endpoints configured')
  }
}

export class OrderTemplatesValidationError extends Error {
  constructor() {
    super('Order templates response failed validation')
  }
}

@registerDependency(FAKE, ORDER_TEMPLATES_STORE)
@registerDependency(REAL, ORDER_TEMPLATES_STORE)
export class OrderTemplatesStore {

  @inject(ORDER_TEMPLATES_API)
  private api!: OrderTemplatesAPI

  @inject(ADD_TO_ORDER_TEMPLATES_API)
  private itemsApi!: AddToOrderTemplatesApi

  orderTemplates: () => OrderTemplates | null
  private setOrderTemplates: (value: OrderTemplates) => void

  loadError: () => unknown
  private setLoadError: (value: unknown) => void

  refetching: () => boolean
  private setRefetching: (value: boolean) => void

  tokenEndpoint = ''
  orderTemplatesEndpoint = ''

  private requested = false
  private loading: Promise<void> | null = null

  constructor() {
    [this.orderTemplates, this.setOrderTemplates] = createSignal<OrderTemplates | null>(null)
    ;[this.loadError, this.setLoadError] = createSignal<unknown>(null)
    ;[this.refetching, this.setRefetching] = createSignal<boolean>(false)
  }

  token = () => ecommerceApiTokenStore.token()

  private get configured() {
    return !!this.tokenEndpoint && !!this.orderTemplatesEndpoint
  }

  configure = (tokenEndpoint: string, orderTemplatesEndpoint: string) => {
    if (!tokenEndpoint || !orderTemplatesEndpoint) return
    if (this.tokenEndpoint === tokenEndpoint && this.orderTemplatesEndpoint === orderTemplatesEndpoint) return

    this.tokenEndpoint = tokenEndpoint
    this.orderTemplatesEndpoint = orderTemplatesEndpoint
    this.loading = null
    if (this.requested) void this.ensureOrderTemplates()
  }

  useDerivedEndpoints = async () => {
    if (this.configured) return
    const [tokenEndpoint, orderTemplatesEndpoint] = await Promise.all([
      createUrl(tokenPath()),
      createUrl(orderTemplatesPath()),
    ])
    if (this.configured) return
    this.configure(tokenEndpoint.href, orderTemplatesEndpoint.href)
  }

  ensureOrderTemplates = (): Promise<void> => {
    this.requested = true
    if (!this.configured) return Promise.resolve()
    if (!this.loading) {
      const loading: Promise<void> = this.refetchOrderTemplates().catch((error: unknown) => {
        if (this.loading === loading) this.loading = null
        this.setLoadError(error)
        console.error('Failed to load order templates', error)
      })
      this.loading = loading
    }
    return this.loading
  }

  refetchOrderTemplates = async () => {
    if (!this.configured) return
    await ecommerceApiTokenStore.ensureToken(this.tokenEndpoint)
    const response = await this.api.getOrderTemplates(
      new GetOrderTemplatesRequest(this.tokenEndpoint, this.orderTemplatesEndpoint)
    )
    const orderTemplatesValidation = validate<OrderTemplates>(response)

    if (orderTemplatesValidation.success) {
      this.setLoadError(null)
      this.setOrderTemplates(orderTemplatesValidation.data)
    } else {
      validationError('OrderTemplates', orderTemplatesValidation.errors)
      if (!this.orderTemplates()) this.setLoadError(new OrderTemplatesValidationError())
    }
  }

  refetchAfter = async (task: () => Promise<void>) => {
    this.setRefetching(true)
    try {
      await task()
      await this.refetchOrderTemplates()
    } finally {
      this.setRefetching(false)
    }
  }

  createOrderTemplate = async (name: string, groupId: number | string, type: string): Promise<{ success: boolean, id?: number }> => {
    if (!this.configured) return { success: false }
    await ecommerceApiTokenStore.ensureToken(this.tokenEndpoint)
    const response = await this.api.createOrderTemplate(
      new CreateOrderTemplateRequest(this.orderTemplatesEndpoint, this.tokenEndpoint, name, groupId, type)
    )
    const orderTemplatesValidation = validate<OrderTemplates>(response)

    if (!orderTemplatesValidation.success) {
      validationError('OrderTemplates', orderTemplatesValidation.errors)
      return { success: false }
    }

    if (!orderTemplatesValidation.data.templates) return { success: false }
    this.setOrderTemplates(orderTemplatesValidation.data)
    const newTemplate = orderTemplatesValidation.data.templates.find(t => t.name === name)
    return newTemplate ? { success: true, id: newTemplate.id } : { success: true }
  }

  addProductToTemplate = async (templateId: number, productId: number, quantity: number): Promise<AddProductToTemplateResponse> => {
    if (!this.configured) throw new OrderTemplatesNotConfiguredError()
    await ecommerceApiTokenStore.ensureToken(this.tokenEndpoint)
    return this.itemsApi.addProductToTemplate(
      new AddProductToTemplateRequest(this.orderTemplatesEndpoint, this.token() ?? '', templateId, productId, quantity)
    )
  }

  removeProductFromTemplate = async (templateId: number, productId: number): Promise<RemoveProductFromTemplateResponse> => {
    if (!this.configured) throw new OrderTemplatesNotConfiguredError()
    await ecommerceApiTokenStore.ensureToken(this.tokenEndpoint)
    return this.itemsApi.removeProductFromTemplate(
      new RemoveProductFromTemplateRequest(this.orderTemplatesEndpoint, this.token() ?? '', templateId, productId)
    )
  }
}
